import moment from 'moment';
import React, { useEffect, useState, useMemo } from 'react';
import { Badge, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Activity,
  ArrowDownUp,
  ArrowUpRightCircle,
  Building,
  Files,
  InfoCircle,
  Pencil,
  Person,
  Trash
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { deleteFileFromServer, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import AlertModal from '../AlertModal';
import FileViewer from '../common/FileViewer';
import UnderlineButton from '../common/UnderlineButton';
import OverViewColumns from '../OverViewColumns';
import UnitCoverImage from './UnitCoverImage';

const filesArePopulated = files =>
  Array.isArray(files) &&
  (files.length === 0 || (typeof files[0] === 'object' && files[0] !== null && !Array.isArray(files[0])));

const minifiedColumnFields = [
  { key: 'manufacturer', label: 'Manufacturer', isObject: true, objectKey: 'factoryShortName' },
  { key: 'Serials', label: 'Serials' },
  { key: 'HUDs', label: 'HUDs' },
  { key: 'sizeA', label: 'Size A' },
  { key: 'sizeB', label: 'Size B' },
  { key: 'sizeOverall', label: 'Size Overall' },
  { key: 'dateOfManufacture', label: 'Date of Manufacture' },
  {
    key: 'model',
    label: 'Model',
    isObject: true,
    objectKey: ['modelName', 'modelNumber']
  },
  { key: 'year', label: 'Year' },
  { key: 'floorplanLender', label: 'Floorplan Lender', isObject: true, objectKey: 'name' },
  { key: 'type', label: 'Type' },
  { key: 'sqFt', label: 'Sq. Ft' },
  { key: 'furnaceManufacturer', label: 'Furnace Manufacturer' },
  { key: 'furnaceModelNumber', label: 'Furnace Model Number' },
  { key: 'btuPerHour', label: 'B.T.U/hr' },
  { key: 'site', label: 'Site', isObject: true, objectKey: 'name' },
  { key: 'currentSlotId', label: 'Slot', isObject: true, objectKey: 'number' }
];

const overviewColumnFields = [
  ...minifiedColumnFields,
  { key: 'bedRooms', label: 'Bedrooms' },
  { key: 'bathRooms', label: 'Bathrooms' }
];

const statusToColor = {
  Stock: 'info',
  Contracted: 'success',
  RSO: 'dark'
};

const getCurtailmentCutOffDate = () => {
  const today = new Date();

  // Set year to previous year
  today.setFullYear(today.getFullYear() - 1);

  // Set to the next month
  today.setMonth(today.getMonth() + 1);

  // Set to the last day of the intended month
  today.setDate(0);

  // Set the time to just before midnight
  today.setHours(23, 59, 59, 999);

  return today.getTime();
};

const InventorySearchItem = ({
  inventory,
  onInventoryClick,
  onChangeClick,
  onRemoveClick,
  onEditClick,
  selected,
  mini = false,
  disabled,
  onAddInventoryPopupMetaChange,
  fromPicker,
  fromMap,
  showLocationDetails,
  onInventoryUpdate,
  coverImageEnabled = false
}) => {
  const [inventoryItem, setInventoryItem] = useState();
  const [togglingMarketed, setTogglingMarketed] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [fileViewerUnit, setFileViewerUnit] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deleteFileModalMeta, setDeleteFileModalMeta] = useState(null);
  const curtailmentCutOffDate = useMemo(() => getCurtailmentCutOffDate(), []);

  useEffect(() => {
    setInventoryItem({
      ...inventory,
      site: inventory?.currentSlotId?.site,
      HUDs:
        ['hudA', 'hudB']
          .map(h => inventory[h])
          .filter(h => h)
          .join(', ') || 'N/A',
      Serials:
        ['serialA', 'serialB']
          .map(h => inventory[h])
          .filter(h => h)
          .join(', ') || 'N/A'
    });
  }, [inventory]);

  let fieldsToShow = mini ? minifiedColumnFields : overviewColumnFields;

  // Add bedroom and bathroom fields only for unit details view in site map
  if (mini && fromMap && showLocationDetails) {
    fieldsToShow = [
      ...minifiedColumnFields,
      { key: 'bedRooms', label: 'Bedrooms' },
      { key: 'bathRooms', label: 'Bathrooms' }
    ];
  }

  const filteredFieldsToShow = fieldsToShow.filter(
    field => !(showLocationDetails && (field.key === 'site' || field.key === 'slot'))
  );

  const handleMarketedToggle = async e => {
    e.stopPropagation();
    if (!inventory?._id || togglingMarketed) return;

    const nextValue = !inventoryItem.isMarketed;
    setTogglingMarketed(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
      requestBody: { isMarketed: nextValue },
      method: 'PUT'
    });

    setTogglingMarketed(false);

    if (error) {
      toast.error(error);
      return;
    }

    const updatedInventory = {
      ...inventory,
      ...response,
      inventoryStatus: inventory.inventoryStatus
    };
    setInventoryItem(prev => ({
      ...prev,
      ...updatedInventory,
      site: updatedInventory?.currentSlotId?.site ?? prev?.site
    }));
    if (onInventoryUpdate) {
      onInventoryUpdate(updatedInventory);
    }
  };

  const showUnitActions = !disabled && (onChangeClick || onRemoveClick || onEditClick);

  const openFileViewer = async () => {
    if (!inventory?._id || loadingFiles) return;

    if (filesArePopulated(inventory.files)) {
      setFileViewerUnit(inventory);
      setShowFileViewer(true);
      return;
    }

    setLoadingFiles(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_SEARCH_NEW,
      requestBody: {
        filter: { _id: inventory._id },
        populate: true,
        includeContract: false,
        includeContact: false,
        returnOnlyInventoryInStock: false,
        pageSize: 1
      }
    });
    setLoadingFiles(false);

    if (error) {
      toast.error(error);
      return;
    }

    const fullUnit = response?.results?.[0];
    if (!fullUnit) {
      toast.error('Unable to load files for this unit');
      return;
    }

    setFileViewerUnit({ ...inventory, ...fullUnit });
    setShowFileViewer(true);
  };

  const deleteFile = async () => {
    if (!deleteFileModalMeta?.file || !fileViewerUnit?._id) return;

    const { file: toBeDeletedFile } = deleteFileModalMeta;
    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: true });

    const newFileArray = (fileViewerUnit.files || []).filter(f => f._id !== toBeDeletedFile._id).map(f => f._id);
    const { response, error } = await makeApiRequests({
      requestBody: { files: newFileArray },
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(fileViewerUnit._id),
      method: 'PUT'
    });

    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: false });
    if (error) {
      return toast.error(error);
    }

    setDeleteFileModalMeta(null);
    deleteFileFromServer(toBeDeletedFile);

    const updatedUnit = {
      ...fileViewerUnit,
      ...(response || {}),
      files: (fileViewerUnit.files || []).filter(f => f._id !== toBeDeletedFile._id)
    };
    setFileViewerUnit(updatedUnit);
    if (onInventoryUpdate) {
      onInventoryUpdate(updatedUnit);
    }
  };

  const handleCoverImageUpdate = updatedInventory => {
    setInventoryItem(prev => ({
      ...prev,
      ...updatedInventory,
      site: updatedInventory?.currentSlotId?.site ?? prev?.site
    }));
    if (onInventoryUpdate) {
      onInventoryUpdate(updatedInventory);
    }
  };

  const renderUnitActionButtons = () =>
    showUnitActions ? (
      <>
        <UnderlineButton
          Icon={Files}
          text="View Files"
          variant="primary"
          fontSize="small"
          iconSize={12}
          disabled={loadingFiles}
          onClick={openFileViewer}
        />
        {onEditClick && (
          <UnderlineButton
            Icon={Pencil}
            text="Edit Unit"
            variant="primary"
            fontSize="small"
            iconSize={12}
            onClick={onEditClick}
          />
        )}
        {onChangeClick && (
          <UnderlineButton
            Icon={ArrowDownUp}
            text="Change Unit"
            variant="primary"
            fontSize="small"
            iconSize={12}
            onClick={onChangeClick}
          />
        )}
        {onRemoveClick && (
          <UnderlineButton
            Icon={Trash}
            text="Remove Unit"
            variant="danger"
            fontSize="small"
            iconSize={12}
            onClick={onRemoveClick}
          />
        )}
      </>
    ) : null;

  return inventoryItem ? (
    <div
      style={{ fontSize: 14 }}
      className={`${onInventoryClick ? 'hover-light' : ''} p-1 mb-2 rounded  ${
        selected ? 'border border-primary' : ''
      }`}
      onClick={e => {
        const cellText = document.getSelection();
        if (cellText.toString().length > 0) {
          return;
        }

        if (onInventoryClick) {
          if (fromMap) {
            // Refactored: Allow the action regardless of setup date when fromMap is true
            onInventoryClick();
          } else {
            if (inventory['contract']) {
              toast.error('This unit has already been contracted!');
            } else {
              onInventoryClick();
            }
          }
        }
      }}
    >
      {showUnitActions && (
        <div className="d-flex d-sm-none justify-content-end mb-1" onClick={e => e.stopPropagation()}>
          {renderUnitActionButtons()}
        </div>
      )}
      {coverImageEnabled && (
        <UnitCoverImage inventory={inventoryItem} onUpdated={handleCoverImageUpdate} />
      )}
      <div className="d-flex">
        <div className="text-center mb-3 p-2 rounded">
          <Building size={25} />
        </div>

        <div className="flex-grow-1 pr-2">
          <div className="d-flex">
            <div className="flex-grow-1">
              {' '}
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="d-inline-block mr-2 mb-0">
                    <Link
                      className=" text-dark"
                      to={'/inventory/' + inventory['_id']}
                      target="_blank"
                      onClick={e => e.stopPropagation()}
                    >
                      {inventory['serialA'] || inventory['serialB']} <ArrowUpRightCircle size={14} />
                    </Link>
                  </h6>
                  {inventory['unitLocation'] && (
                    <Badge className="align-text-bottom" variant={'primary'}>
                      {inventory['unitLocation'].location}
                    </Badge>
                  )}

                  <Badge
                    className="align-text-bottom ml-1"
                    variant={statusToColor[inventory['inventoryStatus']] || 'primary'}
                  >
                    {inventory['inventoryStatus']}
                  </Badge>
                  {inventory?.hasFurnitureInside?.toLowerCase() === 'yes' && (
                    <Badge className="align-text-bottom ml-2" variant="dark">
                      Has Furniture
                    </Badge>
                  )}

                  {inventory?.contract?.buyer && (
                    <Badge
                      className="align-text-bottom ml-1 justify-content-center align-items-center"
                      variant={'primary'}
                    >
                      <Person />
                      <Link to={'/contract/' + inventory.contract._id} style={{ color: 'white' }} target="_blank">
                        {inventory.contract.buyer}
                      </Link>
                    </Badge>
                  )}

                  {inventory.invoiceDate && new Date(inventory.invoiceDate).getTime() < curtailmentCutOffDate && (
                    <Badge className="align-text-bottom ml-1" variant={'warning'}>
                      Curtailment
                    </Badge>
                  )}
                </div>
                {fromMap && (
                  <span
                    className="ml-auto pl-2 d-inline-flex align-items-center flex-shrink-0"
                    onClick={e => e.stopPropagation()}
                  >
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      className="py-0 px-1 d-inline-flex align-items-center"
                      style={{
                        fontSize: '0.7rem',
                        lineHeight: 1.3,
                        gap: '0.2rem',
                        ...(inventoryItem.isMarketed
                          ? {
                              backgroundColor: 'var(--blue)',
                              borderColor: 'var(--blue)',
                              color: '#fff'
                            }
                          : {})
                      }}
                      disabled={togglingMarketed}
                      onClick={handleMarketedToggle}
                    >
                      <Activity size={11} />
                      {inventoryItem.isMarketed ? 'Marketed' : 'Mark as Marketed'}
                    </Button>
                    {inventoryItem.isMarketed && inventoryItem.marketedAt && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`marketed-date-${inventory._id}`}>
                            Marketed on {moment(inventoryItem.marketedAt).format('MMM D, YYYY')}
                          </Tooltip>
                        }
                      >
                        <InfoCircle
                          size={12}
                          className="ml-1 text-muted"
                          style={{ cursor: 'help' }}
                          onClick={e => e.stopPropagation()}
                        />
                      </OverlayTrigger>
                    )}
                  </span>
                )}
                {onAddInventoryPopupMetaChange && (
                  <div className="align-self-center">
                    <Button
                      onClick={e => {
                        e.stopPropagation();
                        onAddInventoryPopupMetaChange({
                          ...inventory,
                          serialA: '',
                          serialB: '',
                          serialC: '',
                          hudA: '',
                          hudB: '',
                          hudC: ''
                        });
                      }}
                      size="sm"
                      variant="success"
                      className={'tinyFont'}
                    >
                      Duplicate
                    </Button>
                  </div>
                )}
              </div>
              <div className="mt-1 d-flex justify-content-between flex-wrap">
                <p className="mb-0  text-secondary">
                  <span className="text-dark">Mfg Date: </span>
                  {inventory['dateOfManufacture']
                    ? moment(inventory['dateOfManufacture']).format('MMMM Do YYYY')
                    : 'N/A'}
                </p>

                <p className="mb-0  text-secondary">
                  <span className="text-dark">Invoice: </span>${inventory['invoice'] || 'N/A'}
                </p>

                <p className="mb-0  text-secondary">
                  <span className="text-dark">Floorplan Finance: </span>${inventory['floorplanFinanceAmount'] || 'N/A'}
                </p>

                <p className="mb-0  text-secondary">
                  <span className="text-dark">Invoice Date: </span>
                  {inventory['invoiceDate'] ? moment(inventory['invoiceDate']).format('MMMM Do YYYY') : 'N/A'}
                </p>

                <p className="mb-0  text-secondary">
                  <span className="text-dark">VEP Code: </span>
                  {inventory['vepCode'] ?? 'N/A'}
                </p>
              </div>
            </div>
            {showUnitActions && (
              <div className="d-none d-sm-flex align-items-start flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                {renderUnitActionButtons()}
              </div>
            )}
          </div>

          <hr className="my-2" />

          <OverViewColumns
            data={inventoryItem}
            fieldsToShow={filteredFieldsToShow}
            dateFields={['dateOfManufacture']}
          />
        </div>
      </div>

      <FileViewer
        sidebarZIndex={2000}
        show={showFileViewer}
        onHide={() => {
          setShowFileViewer(false);
          setFileViewerUnit(null);
        }}
        files={fileViewerUnit?.files || []}
        driveFolder={fileViewerUnit?.driveFolder}
        onFileDeleteClick={file => setDeleteFileModalMeta({ file })}
      />
      <AlertModal
        show={deleteFileModalMeta !== null}
        alertText={`Are you sure to delete this file (${
          deleteFileModalMeta
            ? deleteFileModalMeta.file.metadata && deleteFileModalMeta.file.metadata.originalName
              ? deleteFileModalMeta.file.metadata.originalName
              : deleteFileModalMeta.file.filename
            : ''
        }). You will lose access to this file permanently?`}
        showProgress={deleteFileModalMeta && deleteFileModalMeta.deletingFile}
        progressText="Deleting file..."
        continueButtonText="Delete"
        onDismissClick={() => setDeleteFileModalMeta(null)}
        onHide={() => setDeleteFileModalMeta(null)}
        onContinueClick={deleteFile}
      />
    </div>
  ) : (
    ''
  );
};

export default InventorySearchItem;
